import { HttpInterceptorFn, HttpStatusCode } from '@angular/common/http';
import { inject } from '@angular/core';
import { SessionService } from '../services/session-service';
import { catchError, finalize, Observable, share, switchMap, throwError } from 'rxjs';
import { Router } from '@angular/router';
import { User } from '../models/user';

// One refresh call shared by every request that gets a 401 at the same time,
// so a burst of parallel requests rotates the refresh token only once.
let refreshInFlight: Observable<User> | null = null;

export const authInterceptor: HttpInterceptorFn = (req, next) => {
  const sessionService = inject(SessionService);
  const router = inject(Router);

  // auth is an httpOnly cookie, so every API call must carry credentials
  req = req.clone({ withCredentials: true });

  // login failures must reach the login page, and a failing refresh/logout
  // must not trigger another refresh
  const isAuthEndpoint = req.url.includes('/api/auth/');

  return next(req).pipe(
    catchError((err) => {
      if (err.status !== HttpStatusCode.Unauthorized || isAuthEndpoint) {
        return throwError(() => err);
      }

      if (!refreshInFlight) {
        refreshInFlight = sessionService.refresh().pipe(
          finalize(() => (refreshInFlight = null)),
          share(),
        );
      }

      return refreshInFlight.pipe(
        switchMap(() => next(req)),
        catchError((retryErr) => {
          sessionService.logout();
          router.navigate(['/login'], {
            state: {
              message: 'Session expired. Please log in again.',
            }
          }).then();

          return throwError(() => retryErr);
        }),
      );
    }),
  );
};
